export interface AudioAnalysisFrame {
  level: number;
  spectrum: number[];
}

export const AUDIO_ANALYSIS_BAND_COUNT = 48;

export function configureVoiceAudioAnalyser(analyser: AnalyserNode): void {
  analyser.fftSize = 256;
  analyser.minDecibels = -90;
  analyser.maxDecibels = -12;
  analyser.smoothingTimeConstant = 0;
}

const MINIMUM_DECIBELS = -60;
const MAXIMUM_DECIBELS = -12;
const RELEASE_DURATION_MS = 100;
const FULL_SCALE_FREQUENCY_BYTE = 200;

export function perceptualLevelFromRms(rms: number): number {
  if (!Number.isFinite(rms) || rms <= 0) {
    return 0;
  }

  const decibels = 20 * Math.log10(rms);
  const level =
    (decibels - MINIMUM_DECIBELS) / (MAXIMUM_DECIBELS - MINIMUM_DECIBELS);

  return Math.min(1, Math.max(0, level));
}

export function aggregateFrequencyBins(
  values: Uint8Array,
  bandCount = AUDIO_ANALYSIS_BAND_COUNT,
): number[] {
  if (values.length === 0) {
    return Array(bandCount).fill(0);
  }

  const boundaries = Array.from({ length: bandCount + 1 }, (_, index) =>
    index === bandCount
      ? values.length
      : Math.ceil(Math.pow(values.length, index / bandCount) - 1),
  );

  return Array.from({ length: bandCount }, (_, bandIndex) => {
    const start = boundaries[bandIndex];
    const end = boundaries[bandIndex + 1];
    let peak = 0;

    for (let sourceIndex = start; sourceIndex < end; sourceIndex += 1) {
      peak = Math.max(
        peak,
        Math.min(1, values[sourceIndex] / FULL_SCALE_FREQUENCY_BYTE),
      );
    }

    return peak;
  });
}

export class AudioAnalysisTracker {
  private spectrum = Array(AUDIO_ANALYSIS_BAND_COUNT).fill(0);

  private previousTimestampMs: number | undefined;

  sample(frequencyBytes: Uint8Array, timestampMs: number): AudioAnalysisFrame {
    if (!Number.isFinite(timestampMs)) {
      return this.reset();
    }

    const targetSpectrum = aggregateFrequencyBins(frequencyBytes);
    const elapsedMs = Math.max(
      0,
      timestampMs - (this.previousTimestampMs ?? timestampMs),
    );
    const releaseAmount = elapsedMs / RELEASE_DURATION_MS;

    this.spectrum = targetSpectrum.map((target, index) => {
      if (target >= this.spectrum[index]) {
        return target;
      }

      const released = Math.max(target, this.spectrum[index] - releaseAmount);

      return released <= Number.EPSILON ? 0 : released;
    });
    this.previousTimestampMs = timestampMs;

    return this.frame();
  }

  reset(): AudioAnalysisFrame {
    this.spectrum = Array(AUDIO_ANALYSIS_BAND_COUNT).fill(0);
    this.previousTimestampMs = undefined;

    return this.frame();
  }

  private frame(): AudioAnalysisFrame {
    return {
      level: Math.max(...this.spectrum),
      spectrum: [...this.spectrum],
    };
  }
}
